import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import Card from '@material-ui/core/Card';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import Button from '@material-ui/core/Button';
import Collapse from '@material-ui/core/Collapse';
import EditIcon from '@material-ui/icons/Edit';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import CircularProgress from '@material-ui/core/CircularProgress';
import { makeStyles } from '@material-ui/core/styles';
import { toastr } from 'react-redux-toastr';
import cache from 'utilities/cache';
import moment from 'moment';
import shortuuid from 'short-uuid';

import { formatTime, formatDatetime, formatNote } from 'utilities/format';
import UserAvatar from 'components/UserAvatar';
import LinearProgressWithLabel from 'components/LinearProgressWithLabel';
import { request } from 'utilities/graph';
import { groupPurchaseClientQueryOrderAmount, individualClientUpdateOrder } from 'graphql/mutations';

import {
  getDeliveryStaffOrdersByOrder,
  getOrderEventsByOrderByCreatedAt,
} from './queries';
import { getRestaurant } from 'graphql/queries';
import { sortBy } from 'utilities/sorting';
import { asyncListAll } from 'utilities/graph';
import OrderEventCard from 'components/Order/OrderEventCard';
import DeliveryStaffOrderPhoto from 'components/DeliveryStaffOrderPhoto';
import { groupPurchaseStatus as groupPurchaseStatusConstants } from '@silvergatedelivery/constants';
import DietaryNoteDialog from './DietaryNoteDialog';

const getProgressColor = (value, min, max) => {
  if (value >= max) { // Check max > 0 for "no limit" cases
    return '#f05a5a'; // red - 達到上限
  } else if (value < min) {
    return '#f8aa5d'; // orange - 未達到最小訂單量
  } else {
    return '#00913A'; // green - 在最小和最大訂單量之間
  }
};

const useStyles = makeStyles((theme) => ({
  bold: {
    fontWeight: 'bold',
  },
  statusContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100%',
  },
  statusText: {
    padding: '8px 16px',
    borderRadius: '4px',
    border: '2px solid',
    textAlign: 'center',
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
  },
  progressContainer: {
    flex: 1,
    minWidth: 0,
    marginTop: '8px',
  },
  progress: {
    'height': 8, // Use number for pixel value
    'borderRadius': 4, // Use number for pixel value
    '& .MuiLinearProgress-bar': {
      backgroundColor: (props) => {
        return getProgressColor(props.value, props.min, props.max);
      },
    },
  },
}));

export default function OrderCard({ order: orderProp = {}, updateOrder }) {
  const [order, setOrder] = React.useState(orderProp);
  useEffect(() => {
    setOrder(orderProp);
  }, [orderProp]);
  const [receivedOrderAmount, setReceivedOrderAmount] = useState(0);
  const [minOrderQuantity, setMinOrderQuantity] = useState(0);
  const [maxOrderQuantity, setMaxOrderQuantity] = useState(0);
  const [dietaryNotesDeadlineHours, setDietaryNotesDeadlineHours] = useState(0);
  const [isLoading, setIsLoading] = useState(false);

  const progressStylingProps = { value: receivedOrderAmount, min: minOrderQuantity, max: maxOrderQuantity };
  const classes = useStyles(progressStylingProps);

  const [deliveryStaffOrder, setDeliveryStaffOrder] = useState();
  const [orderEvents, setOrderEvents] = useState([]);
  const [lastUpdatedAt, setLastUpdatedAt] = useState(Date.now());
  const [mealsTotal, setMealsTotal] = useState(0);

  const [expanded, setExpanded] = useState(true);
  const [dietaryNotes, setDietaryNotes] = useState([]);
  const [isDietaryNoteDirty, setIsDietaryNoteDirty] = useState(false);
  const [eventsExpanded, setEventsExpanded] = useState(false);
  const [groupProgressExpanded, setGroupProgressExpanded] = useState(false);

  const dietaryNotesDeadlineDateTime = moment(order.deliveryBy).subtract(dietaryNotesDeadlineHours, 'hours');
  const canEditDietaryNotes = order.groupPurchaseStatus === 'accepted' && Date.now() < dietaryNotesDeadlineDateTime.valueOf();

  const {
    deliveryBy,
    deliveryStaffFee,
    extraFee = 0,
    platformFee = 0,
    groupPurchaseStatus,
    date,
    mealSlot,
    restaurantId,
    groupPurchaseDietaryNote,
  } = order;

  useEffect(() => {
    (async () => {
      setIsLoading(true);
      const [
        deliveryStaffOrders,
        orderEventsData,
      ] = await Promise.all([
        asyncListAll(getDeliveryStaffOrdersByOrder, { orderId: order.id }),
        asyncListAll(getOrderEventsByOrderByCreatedAt, { orderId: order.id }),
      ]);
      global.logger.debug({ deliveryStaffOrders, orderEvents: orderEventsData });
      setDeliveryStaffOrder(deliveryStaffOrders.sort(sortBy('createdAt', true))[0]);
      setOrderEvents(orderEventsData.filter(({ content }) => content !== ''));
      // order 裡面沒有子女應付送餐費用, 先用全部費用減去餐費計算
      setMealsTotal(order.mealItems.reduce((pre, { price, quantity }) => {
        return pre + (price * quantity);
      }, 0));

      try {
        const { data: { getRestaurant: data } } = await request(getRestaurant, { id: restaurantId });
        if (data && typeof data.minOrderQuantity === 'number') {
          setMinOrderQuantity(data.minOrderQuantity);
        }
        if (data && typeof data.maxOrderQuantity === 'number') {
          setMaxOrderQuantity(data.maxOrderQuantity);
        }

        if (data && typeof data.dietaryNotesDeadlineHours === 'number') {
          setDietaryNotesDeadlineHours(data.dietaryNotesDeadlineHours);
        }
      } catch (error) {
        global.logger.error('Error fetching restaurant', error);
      }

      // 未接單
      if (groupPurchaseStatus === 'pending' && restaurantId && date && mealSlot) {
        try {
          const { data: { groupPurchaseClientQueryOrderAmount: result } } = await request(groupPurchaseClientQueryOrderAmount, {
            input: {
              restaurantId: restaurantId,
              date: date,
              mealSlot: mealSlot,
            },
          }, 'API_KEY');
          if (result && typeof result.totalQuantity === 'number') {
            setReceivedOrderAmount(result.totalQuantity);
          }
        } catch (error) {
          global.logger.error('Error fetching group purchase order amount', error);
        }
      }

      // 已接單或已拒單都帶入團體飲食備註
      if ((groupPurchaseStatus === 'accepted' || groupPurchaseStatus === 'abandoned') && groupPurchaseDietaryNote) {
        setDietaryNotes(groupPurchaseDietaryNote.split('; '));
      }


      setIsLoading(false);
    })();
  }, [order, lastUpdatedAt, groupPurchaseStatus, restaurantId, date, mealSlot, groupPurchaseDietaryNote]);


  const getStatusStyle = (status) => {
    const statusInfo = groupPurchaseStatusConstants.find(({ value }) => value === status);

    if (!statusInfo) {
      return {
        color: '#000000',
        borderColor: '#cccccc',
        backgroundColor: '#f0f0f0',
      };
    }

    return {
      color: statusInfo.colorText,
      borderColor: statusInfo.colorText,
      backgroundColor: statusInfo.backgroundColor,
    };
  };

  const handleDietaryNotesSubmit = async (updatedNotes) => {
    global.logger.debug('handleDietaryNotesSubmit', updatedNotes);

    const currentUsername = cache.get('app:username');
    const currentClientId = cache.get('app:clientId');
    const newNotes = updatedNotes.join('; ');

    try {
      const { data: { individualClientUpdateOrder: result } } = await request(individualClientUpdateOrder, {
        input: {
          clientId: currentClientId,
          clientUsername: currentUsername,
          orderId: order.id,
          groupPurchaseDietaryNote: newNotes,
        },
      });
      if (result && result.message === 'success') {
        toastr.success('飲食備註已更新');
        setOrder((o) => ({ ...o, groupPurchaseDietaryNote: newNotes }));
        updateOrder && updateOrder(order.id, { groupPurchaseDietaryNote: newNotes });
        setExpanded(false);
      } else {
        toastr.error('更新飲食備註失敗');
      }
    } catch (error) {
      toastr.error('更新飲食備註失敗');
    }
  };

  const onCancelOrder = async () => {
    const confirmed = window.confirm('您確定要取消這筆訂單嗎？');
    if (!confirmed) {
      return;
    }
    const currentUsername = cache.get('app:username');
    const currentClientId = cache.get('app:clientId');

    try {
      const { data: { individualClientUpdateOrder: result } } = await request(individualClientUpdateOrder, {
        input: {
          clientId: currentClientId,
          clientUsername: currentUsername,
          orderId: order.id,
          groupPurchaseStatus: 'clientCancelled',
        },
      });
      if (result && result.message === 'success') {
        toastr.success('訂單已取消');
        setOrder((o) => ({ ...o, groupPurchaseStatus: 'clientCancelled' }));
        updateOrder && updateOrder(order.id, { groupPurchaseStatus: 'clientCancelled' });
      } else {
        toastr.error('取消訂單失敗');
      }
    } catch (error) {
      toastr.error('取消訂單失敗');
    }
  };

  const handleToggleDietaryNote = () => {
    if (expanded && isDietaryNoteDirty) {
      if (window.confirm('輸入內容未送出，確定要關閉嗎？')) {
        setExpanded(false);
      }
    } else {
      setExpanded(!expanded);
    }
  };

  return (
    <Card style={{ padding: 16 }}>
      <Grid container spacing={2}>
        {isLoading ?
          <Grid item xs={12} container justifyContent="center" alignItems="center">
            <CircularProgress />
          </Grid> :
          <React.Fragment>
            <Grid item xs={6} onClick={() => setLastUpdatedAt(Date.now())}>
              <Typography variant="h6" color="primary" className={classes.bold}>
                {formatDatetime(deliveryBy, { year: false, hour: false, minute: false })}
              </Typography>
              <Typography variant="h5" color="primary" className={classes.bold}>
                {formatTime(deliveryBy)} - {formatTime(moment(deliveryBy).add(1, 'hours'))}
              </Typography>
              <Typography component="p" variant="body2" color="textSecondary">
                餐點送達時段
              </Typography>
            </Grid>
            <Grid item xs={6}>
              {groupPurchaseStatus && (
                <div className={classes.statusContainer}>
                  <Typography
                    variant="h5"
                    className={classes.statusText}
                    style={getStatusStyle(groupPurchaseStatus)}
                  >
                    {groupPurchaseStatusConstants.find(({ value }) => value === groupPurchaseStatus)?.label || groupPurchaseStatus}
                  </Typography>
                </div>
              )}
            </Grid>
            <Grid item xs={12}>
              {groupPurchaseStatus === 'pending' && restaurantId && (
                <React.Fragment>
                  <Grid container justifyContent="space-between" alignItems="center" style={{ marginTop: '3rem', marginBottom: '1rem' }}>
                    <Typography variant="h6" color="textPrimary" className={classes.bold}>
                      集單進度
                    </Typography>
                    <Button
                      size="large"
                      onClick={() => setGroupProgressExpanded(!groupProgressExpanded)}
                      endIcon={groupProgressExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    >
                      {groupProgressExpanded ? '收起' : '展開'}
                    </Button>
                  </Grid>
                  <Collapse in={groupProgressExpanded} timeout="auto" unmountOnExit>
                    <div className={classes.progressContainer}>
                      <LinearProgressWithLabel
                        value={receivedOrderAmount}
                        max={maxOrderQuantity}
                        className={classes.progress}
                      />
                      <Typography variant="subtitle1" color="textSecondary" align="center">
                        {(() => {
                          let progressText = `目前已募集 ${receivedOrderAmount} 份`;
                          if (maxOrderQuantity > 0) {
                            progressText += ` ➡️ 目標 ${minOrderQuantity} 份`;
                          } else {
                            progressText += ` (無上限)`;
                          }
                          if (minOrderQuantity > 0 && receivedOrderAmount < minOrderQuantity) {
                            progressText += ` (還差 ${minOrderQuantity - receivedOrderAmount} 份達標)`;
                          } else if (minOrderQuantity > 0 && receivedOrderAmount >= minOrderQuantity) {
                            progressText += ` (已達標🎉)`;
                          }
                          return progressText;
                        })()}
                      </Typography>
                    </div>
                  </Collapse>
                </React.Fragment>
              )}
              {(groupPurchaseStatus === 'accepted' || groupPurchaseStatus === 'abandoned') && (
                <Grid item xs={12}>
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '0 16px', gap: '1rem' }}>
                    {<Button
                      variant="contained"
                      color={canEditDietaryNotes ? 'secondary' : 'default'}
                      startIcon={<EditIcon />}
                      onClick={handleToggleDietaryNote}
                      style={{ fontSize: '16px' }}
                    >
                      {expanded ? '收起飲食備註' : (canEditDietaryNotes ? '編輯飲食備註' : '查看飲食備註')}
                    </Button>}
                    {canEditDietaryNotes && (
                      <Typography variant="h6" style={{ color: '#f05a5a', marginTop: '8px', textAlign: 'center', fontWeight: 'bold' }}>
                        請於{formatDatetime(dietaryNotesDeadlineDateTime, { year: false, weekday: false } )}前完成備註
                      </Typography>
                    )}
                  </div>
                  <Collapse in={expanded} timeout="auto" unmountOnExit>
                    <Grid container justifyContent="center">
                      {expanded &&
                        <DietaryNoteDialog
                          notes={dietaryNotes}
                          setNotes={setDietaryNotes}
                          handleSubmitBtnClicked={handleDietaryNotesSubmit}
                          viewOnly={!canEditDietaryNotes}
                          onDirtyChange={setIsDietaryNoteDirty} />}
                    </Grid>
                  </Collapse>
                </Grid>
              )}
              <Grid item xs={12} style={{ margin: '3rem 0' }}>
                <Grid container justifyContent="space-between" style={{ marginBottom: '1rem' }}>
                  <Typography variant="h5" align="left" className={classes.bold}>
                    訂購內容
                  </Typography>
                  <Typography variant="h6" align="right" color="textSecondary">
                    編號 {shortuuid().fromUUID(order.id).slice(-6).toUpperCase()}
                  </Typography>
                </Grid>
                <Grid container justifyContent="space-between">
                  <Typography variant="h6" align="left" color="textSecondary">
                    {order.restaurant.name || ''}
                  </Typography>
                </Grid>
                {order.mealItems.map(({ name, quantity, price }, index) => (
                  <Grid container justifyContent="space-between" key={index}>
                    <Typography variant="h6" align="left" color="textSecondary">
                      {name} x{quantity}
                    </Typography>
                    <Typography variant="h6" align="right" color="textSecondary">
                      ${price * quantity}
                    </Typography>
                  </Grid>
                ))}
                <Grid container justifyContent="space-between">
                  <Typography variant="h6" align="left" color="textSecondary">
                    送餐費用
                  </Typography>
                  <Typography variant="h6" align="right" color="textSecondary">
                    ${deliveryStaffFee + extraFee + platformFee}
                  </Typography>
                </Grid>
                <Grid container justifyContent="flex-end" style={{ borderTop: '1px solid #cececece' }}>
                  <Typography variant="h6" align="right" color="textPrimary" className={classes.bold}>
                    訂單總金額： ${mealsTotal + deliveryStaffFee + extraFee + platformFee}
                  </Typography>
                </Grid>
              </Grid>
              <Grid item xs={12}>
                <Typography component="p" variant="body1" color="textSecondary" className={classes.bold}>
                  送餐大使
                </Typography>
                {deliveryStaffOrder ?
                  <UserAvatar
                    username={deliveryStaffOrder.deliveryStaff.username}
                    name={deliveryStaffOrder.deliveryStaff.name}
                  /> :
                  <Typography component="p" variant="body2" color="textSecondary">
                    未指派
                  </Typography>}
              </Grid>
              <Grid item xs={12}>
                <Grid container justifyContent="space-between" alignItems="center" style={{ marginTop: '3rem', marginBottom: '1rem' }}>
                  <Typography variant="h6" color="textPrimary" className={classes.bold}>
                    訂單進度
                  </Typography>
                  <Button
                    size="large"
                    onClick={() => setEventsExpanded(!eventsExpanded)}
                    endIcon={eventsExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  >
                    {eventsExpanded ? '收起' : '展開'}
                  </Button>
                </Grid>
                <Collapse in={eventsExpanded} timeout="auto" unmountOnExit>
                  <OrderEventCard
                    events={orderEvents}
                    timelineOnly={true}
                    hideDetails={true}
                    hideSource={true}
                  />
                </Collapse>
              </Grid>
              {(groupPurchaseStatus === 'pending') && (
                <Grid item xs={12}>
                  <Grid container justifyContent="flex-end">
                    <Button variant="contained" color="secondary" onClick={onCancelOrder} style={{ fontSize: '16px' }} >
                      取消訂單
                    </Button>
                  </Grid>
                </Grid>
              )}
              {deliveryStaffOrder && deliveryStaffOrder.status === 'completed' && deliveryStaffOrder.photoS3Keys &&
                <Grid item xs={12}>
                  <Typography component="p" variant="body1" color="textPrimary" className={classes.bold}>
                    送餐關懷
                  </Typography>
                  <div style={{ height: 150 }}>
                    <DeliveryStaffOrderPhoto s3Key={deliveryStaffOrder.photoS3Keys[0]} />
                  </div>
                  <Typography component="p" variant="body2" color="textSecondary">
                    {formatNote(deliveryStaffOrder.diary || '無')}
                  </Typography>
                </Grid>}
            </Grid>
          </React.Fragment>
        }
      </Grid>
    </Card>
  );
}

OrderCard.propTypes = {
  order: PropTypes.object,
  updateOrder: PropTypes.func,
};
